import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import { fieldErrorsOf } from 'shared/apolloFieldErrors';

// A partner API key as an admin sees it. The key itself is never listed
// (the API keeps only its hash); key_prefix tells keys apart.
export const API_KEY_FIELDS = gql`
  fragment apiKeyFields on ApiKey {
    id
    account_id
    name
    key_prefix
    scopes
    created_at
    last_used_at
    expires_at
    revoked_at
  }
`;

// Newest first, revoked keys included.
export const GET_API_KEYS = gql`
  query GetApiKeys($account_id: Int!) {
    getApiKeys(account_id: $account_id) {
      ...apiKeyFields
    }
  }
  ${API_KEY_FIELDS}
`;

const NO_KEYS = [];

// { apiKeys, loading, error }: error is the message to show, or null.
export function useGetApiKeys(accountId) {
  const { data, loading, error } = useQuery(GET_API_KEYS, {
    variables: { account_id: accountId },
    fetchPolicy: 'cache-and-network',
  });
  return {
    apiKeys: (data && data.getApiKeys) || NO_KEYS,
    loading,
    error: error ? fieldErrorsOf(error).global : null,
  };
}

// Runs a create or revoke mutation and refetches the account's keys.
// Resolves { success: true, data } with data the mutation's `field`, or
// { success: false, error } with the message for the Toaster. The refetch
// is not awaited: a failed refetch must never hide a key that was created.
export async function mutateApiKeys(mutate, accountId, variables, field) {
  try {
    const result = await mutate({
      variables,
      refetchQueries: [
        { query: GET_API_KEYS, variables: { account_id: accountId } },
      ],
    });
    return { success: true, data: result.data[field] };
  } catch (error) {
    return { success: false, error: fieldErrorsOf(error).global };
  }
}
